import { useCallback, useMemo, useState, type FormEvent } from 'react';
import type { ScanJob, ScanJobInput } from '@detect/shared';
import { scanJobService } from '../../services/scan-jobs';
import type { WithId } from '../../services/types';
import { useCursorList } from '../../hooks/useCursorList';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import { clientService } from '../../services/clients';
import { collectorService } from '../../services/collectors';
import { useTenant } from '../../contexts/TenantContext';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';
import { collectorOptionLabel } from '../collectors/channel';

/** Network discovery scan jobs (PRD §2.2, LM NetScan equivalent). */
export function DiscoveryPage() {
  const { tenantId, hasRole } = useTenant();
  const [editing, setEditing] = useState<WithId<ScanJob> | 'new' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [ranNotice, setRanNotice] = useState<string | null>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      scanJobService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<WithId<ScanJob>>(fetchPage, 'all');

  const runNow = async (job: WithId<ScanJob>) => {
    if (!tenantId) return;
    setActionError(null);
    setRanNotice(null);
    try {
      await scanJobService.run(tenantId, job.id);
      setRanNotice(`Scan "${job.name}" queued — results appear as resources/instances.`);
    } catch (err) {
      setActionError(err);
    }
  };

  const remove = async (job: WithId<ScanJob>) => {
    if (!tenantId) return;
    if (!window.confirm(`Delete scan job ${job.name}?`)) return;
    setActionError(null);
    try {
      await scanJobService.remove(tenantId, job.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Discovery</h1>
        {hasRole('agent') && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditing('new')}
          >
            New scan job
          </button>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}
      {ranNotice && <div className="notice">{ranNotice}</div>}

      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No scan jobs"
          description="Create a scan job to sweep a network range from a collector and discover resources."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Ranges</th>
                <th>Interval</th>
                <th>Auto-add</th>
                <th>Enabled</th>
                {hasRole('agent') && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.items.map((job) => (
                <tr key={job.id}>
                  <td>{job.name}</td>
                  <td>{job.cidrRanges.join(', ')}</td>
                  <td>{job.intervalHours ? `${job.intervalHours}h` : 'manual'}</td>
                  <td>{job.autoAdd ? 'yes' : 'no'}</td>
                  <td>{job.enabled ? 'yes' : 'no'}</td>
                  {hasRole('agent') && (
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={!job.enabled}
                        title={job.enabled ? undefined : 'Enable the job to run it'}
                        onClick={() => void runNow(job)}
                      >
                        Run now
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setEditing(job)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(job)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />

      {editing !== null && (
        <ScanJobModal
          job={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void list.reload();
          }}
        />
      )}
    </div>
  );
}

function ScanJobModal({
  job,
  onClose,
  onSaved,
}: {
  job: WithId<ScanJob> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId, clientScope } = useTenant();
  const [name, setName] = useState(job?.name ?? '');
  const [collectorId, setCollectorId] = useState(job?.collectorId ?? '');
  const [ranges, setRanges] = useState(job?.cidrRanges.join(', ') ?? '');
  const [intervalHours, setIntervalHours] = useState(
    job?.intervalHours ? String(job.intervalHours) : ''
  );
  const [autoAdd, setAutoAdd] = useState(job?.autoAdd ?? false);
  const [clientId, setClientId] = useState(job?.clientId ?? '');
  const [enabled, setEnabled] = useState(job?.enabled ?? true);
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const collectorsQuery = usePickerOptions(['collectors'], (tid, startAfter) =>
    collectorService.list(tid, { limit: 100, startAfter })
  );
  const clientsQuery = usePickerOptions(['clients'], (tid, startAfter) =>
    clientService.list(tid, { limit: 100, startAfter })
  );
  const clients = useMemo(
    () =>
      (clientsQuery.data?.items ?? []).filter(
        (c) => clientScope === null || clientScope.includes(c.id)
      ),
    [clientsQuery.data, clientScope]
  );
  // Collector/client affinity (API-enforced): a client-bound collector
  // can only scan for its own client; shared collectors scan anything.
  const collectors = useMemo(
    () =>
      (collectorsQuery.data?.items ?? []).filter(
        (c) => c.clientId === null || (clientId !== '' && c.clientId === clientId)
      ),
    [collectorsQuery.data, clientId]
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSubmitting(true);
    setError(null);
    const cidrRanges = ranges
      .split(',')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);
    const payload: ScanJobInput = {
      name,
      collectorId,
      cidrRanges,
      intervalHours: intervalHours ? Number(intervalHours) : null,
      autoAdd,
      clientId: clientId || null,
      enabled,
    };
    try {
      if (job) {
        await scanJobService.update(tenantId, job.id, payload as unknown as Record<string, unknown>);
      } else {
        await scanJobService.create(tenantId, payload);
      }
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal title={job ? `Edit ${job.name}` : 'New scan job'} onClose={onClose}>
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="scan-name">Name</label>
          <input
            id="scan-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="scan-collector">Collector</label>
          <select
            id="scan-collector"
            name="collectorId"
            required
            value={collectorId}
            onChange={(e) => setCollectorId(e.target.value)}
          >
            <option value="">— select —</option>
            {collectors.map((c) => (
              <option key={c.id} value={c.id}>
                {collectorOptionLabel(c, true)}
              </option>
            ))}
          </select>
          {collectorsQuery.data?.truncated && (
            <p className="text-muted">Collector list truncated.</p>
          )}
        </div>
        <div className="form-field">
          <label htmlFor="scan-ranges">CIDR ranges (comma-separated, /16 or smaller)</label>
          <input
            id="scan-ranges"
            name="cidrRanges"
            required
            placeholder="10.0.0.0/24, 192.168.1.0/24"
            value={ranges}
            onChange={(e) => setRanges(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="scan-interval">Re-scan interval (hours; empty = manual)</label>
          <input
            id="scan-interval"
            name="intervalHours"
            type="number"
            min={1}
            max={720}
            value={intervalHours}
            onChange={(e) => setIntervalHours(e.target.value)}
          />
        </div>
        <div className="form-field form-field-inline">
          <input
            id="scan-autoadd"
            name="autoAdd"
            type="checkbox"
            checked={autoAdd}
            onChange={(e) => setAutoAdd(e.target.checked)}
          />
          <label htmlFor="scan-autoadd">Auto-add discovered resources</label>
        </div>
        {autoAdd && (
          <div className="form-field">
            <label htmlFor="scan-client">Client for auto-added resources</label>
            <select
              id="scan-client"
              name="clientId"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              <option value="">— none (tenant-level) —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="form-field form-field-inline">
          <input
            id="scan-enabled"
            name="enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label htmlFor="scan-enabled">Enabled</label>
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Saving…' : job ? 'Save' : 'Create'}
        </button>
      </form>
    </Modal>
  );
}
